import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BadgeCheck, MapPin, Calendar, Link as LinkIcon, Users, FileText, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PublicProfile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      // Fetch public profile data
      const response = await axios.get(`${API_URL}/users/public/${username}`);
      setProfile(response.data.user);
      setPosts(response.data.posts || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Profile not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
        <Card className="p-8 text-center bg-slate-800/50 border-slate-700 max-w-md">
          <h1 className="text-2xl font-bold text-white mb-4">Profile Not Found</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-purple-600 hover:bg-purple-700 text-white rounded-full">
              Go to Socyy
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{profile?.name || profile?.username} (@{profile?.username}) - Socyy</title>
        <meta name="description" content={profile?.bio || `Follow ${profile?.name || profile?.username} on Socyy - The social platform that puts you first.`} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${profile?.name || profile?.username} (@${profile?.username}) - Socyy`} />
        <meta property="og:description" content={profile?.bio || `Follow ${profile?.name || profile?.username} on Socyy`} />
        <meta property="og:image" content={profile?.avatar || '/icons/icon-512x512.png'} />
        <meta property="og:url" content={`${window.location.origin}/u/${username}`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${profile?.name || profile?.username} - Socyy`} />
        <meta name="twitter:description" content={profile?.bio || `Connect with ${profile?.name || profile?.username} on Socyy - Your people, your world.`} />
        <link rel="canonical" href={`${window.location.origin}/u/${username}`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pb-20">
        {/* Header */}
        <div className="bg-slate-800/50 border-b border-slate-700">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <Link to="/" className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Socyy</span>
            </Link>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 pt-8">
          {/* Profile Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-8 bg-slate-800/50 border-slate-700 rounded-3xl mb-6">
              {/* Cover Photo */}
              {profile?.cover_photo && (
                <div className="h-40 -m-8 mb-0 rounded-t-3xl overflow-hidden">
                  <img src={profile.cover_photo} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 -mt-12 relative z-10">
                <Avatar className="w-28 h-28 border-4 border-slate-800 shadow-xl">
                  <AvatarImage src={profile?.avatar} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-600 to-pink-600 text-white text-3xl font-bold">
                    {profile?.name?.charAt(0) || profile?.username?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                    <h1 className="text-2xl font-bold text-white">{profile?.name}</h1>
                    {profile?.is_verified && (
                      <BadgeCheck className="w-6 h-6 text-purple-500" />
                    )}
                  </div>
                  <p className="text-gray-400">@{profile?.username}</p>
                </div>
                
                <Link to={`/login?redirect=/profile/${profile?.user_id}`}>
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-8">
                    Follow on Socyy
                  </Button>
                </Link>
              </div>
              
              {/* Bio */}
              {profile?.bio && (
                <p className="text-gray-300 mt-6 text-center sm:text-left">{profile.bio}</p>
              )}
              
              {/* Stats & Info */}
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6 mt-6 text-sm text-gray-400">
                {profile?.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {profile.location}
                  </span>
                )}
                {profile?.website && (
                  <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-purple-400 hover:text-purple-300">
                    <LinkIcon className="w-4 h-4" />
                    {profile.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Joined {profile?.created_at ? formatDistanceToNow(new Date(profile.created_at), { addSuffix: true }) : 'recently'}
                </span>
              </div>
              
              {/* Follow Stats */}
              <div className="flex items-center justify-center sm:justify-start gap-6 mt-4">
                <span className="text-white">
                  <strong>{profile?.followers_count || 0}</strong>
                  <span className="text-gray-400 ml-1">Followers</span>
                </span>
                <span className="text-white">
                  <strong>{profile?.following_count || 0}</strong>
                  <span className="text-gray-400 ml-1">Following</span>
                </span>
                <span className="text-white">
                  <strong>{profile?.posts_count || 0}</strong>
                  <span className="text-gray-400 ml-1">Posts</span>
                </span>
              </div>
            </Card>
          </motion.div>

          {/* Public Posts Preview */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              Recent Posts
            </h2>
            
            {posts.length === 0 ? (
              <Card className="p-8 bg-slate-800/50 border-slate-700 rounded-2xl text-center">
                <p className="text-gray-400">No public posts yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.slice(0, 5).map((post, idx) => (
                  <motion.div
                    key={post.post_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card className="p-6 bg-slate-800/50 border-slate-700 rounded-2xl hover:border-purple-500/50 transition-colors">
                      <p className="text-gray-300 mb-3">{post.content}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{post.likes_count || 0} likes</span>
                        <span>{post.comments_count || 0} comments</span>
                        <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <Card className="p-8 bg-gradient-to-br from-purple-900/50 to-pink-900/50 border-purple-500/30 rounded-3xl text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Join Socyy</h2>
            <p className="text-gray-300 mb-6">
              The social platform that puts you first. No bots, no fakes, just real connections.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button className="bg-white text-purple-900 hover:bg-gray-100 rounded-full px-8 font-bold">
                  Sign Up Free
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8">
                  Log In
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default PublicProfile;
